import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSAsyncAwait: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Async / Await</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Syntactic sugar over promises that lets asynchronous code read like synchronous code — top to bottom, with ordinary try/catch. It doesn&apos;t replace promises; it&apos;s built on them.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="17 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Promises cleaned up callbacks, but long workflows still demand mental bookkeeping. Consider a chain of business steps:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `getUser()\n  .then(user => getOrders(user.id))\n  .then(orders => getPayments(orders))\n  .then(payments => generateReport(payments))\n  .then(report => sendEmail(report));` }]} defaultLanguage="javascript" id="aa-chain" />
   <Para>This is manageable at four steps. At twenty or thirty, tracking returned values and error flow across <Code>.then()</Code> callbacks gets hard. JavaScript needed a way to make async code <Mark>look like synchronous code</Mark>. The solution is <Mark>async/await</Mark>.</Para>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why Existing Solutions Fail</SectionHeading>
   <Para>Promises solved callback hell, but a readable chain still forces you to mentally track several things at once:</Para>
   <div className="flex flex-wrap gap-2 mb-4">{['Promise chains', 'Returned values', 'Nested conditions', 'Error flow'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>Most developers naturally wonder: <Mark>&quot;Can I just write this top to bottom, like normal code?&quot;</Mark> That is exactly the problem async/await was introduced to solve.</Para>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Async/await</Mark> is syntactic sugar built on top of promises that lets asynchronous code be written in a synchronous-looking style.</p>
   </div>
   <Callout tone="warning" title="It does not replace promises">Async/await is built on promises, not a substitute for them.</Callout>
   <Para>Think of it as: <Mark>promises are the engine; async/await is a better dashboard.</Mark></Para>
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>The Core Idea</SectionHeading>
   <Para>The same logic, rewritten so the flow reads linearly:</Para>
   <TwoUp
    left={{ title: 'Promise chain', items: ['getUser().then(...)', '.then(orders => ...)', 'Flow jumps between callbacks'] }}
    right={{ title: 'Async / await', items: ['const user = await getUser()', 'const orders = await getOrders(user.id)', 'Reads top to bottom'] }}
   />
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = await getUser();\nconst orders = await getOrders(user.id);\nconsole.log(orders);` }]} defaultLanguage="javascript" id="aa-core" />
  </section>

  {/* 5. Internal Working: async + await */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <SubHeading>The async keyword</SubHeading>
   <Para>Marking a function <Code>async</Code> means it <Mark>always returns a promise</Mark> — even when you return a plain value:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `async function greet() {\n  return "Hello";\n}\n\n// JavaScript treats it as:\nfunction greet() {\n  return Promise.resolve("Hello");\n}` }]} defaultLanguage="javascript" id="aa-async" />
   <SubHeading>The await keyword</SubHeading>
   <Para><Code>await</Code> pauses <Mark>only the current async function</Mark> until the promise settles — it does <em>not</em> pause the JavaScript runtime. The engine is free to run other work while this function is suspended:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `Start async function\n   ↓\nawait getUser()\n   ↓\nSuspend THIS function (engine continues other work)\n   ↓\nPromise settles\n   ↓\nResume this function` }]} defaultLanguage="text" id="aa-await-flow" />
  </section>

  {/* 6. Visual Flow: is await blocking? */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow: Is await Blocking?</SectionHeading>
   <Para>A favourite interview question. The answer is <Mark>yes and no</Mark>:</Para>
   <TwoUp
    left={{ title: 'It blocks', items: ['Only the current async function', 'Lines after await wait for the promise'] }}
    right={{ title: 'It does NOT block', items: ['The JavaScript runtime', 'The event loop', 'The browser / Node.js'] }}
   />
   <CodeBlock snippets={[{ language: 'text', code: `Current function → waiting at await\nOther tasks      → keep running (timers, events, requests)` }]} defaultLanguage="text" id="aa-blocking" />
  </section>

  {/* 7. Simple Example */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example</SectionHeading>
   <Para>Every async function returns a promise — proof:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `async function greet() {\n  return "Hello";\n}\n\nconsole.log(greet());` }]} defaultLanguage="javascript" id="aa-proof" />
   <Output>{`Promise { 'Hello' }`}</Output>
   <Para>Not <Code>Hello</Code> — a promise wrapping it. The same logic as a promise chain, side by side:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `// Promise version\ngetUser().then(user => {\n  console.log(user);\n});\n\n// Async/await version\nasync function run() {\n  const user = await getUser();\n  console.log(user);\n}` }]} defaultLanguage="javascript" id="aa-simple" />
  </section>

  {/* 8. Deep Example: converting chains + try/catch */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Converting Chains</SectionHeading>
   <Para>A promise chain becomes a flat sequence of <Code>await</Code> statements:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `async function processOrder() {\n  const order = await createOrder();\n  const payment = await processPayment(order);\n  const driver = await assignDriver(payment);\n  await sendNotification(driver);\n}` }]} defaultLanguage="javascript" id="aa-convert" />
   <SubHeading>Error handling with try/catch</SubHeading>
   <Para>Instead of <Code>.catch()</Code>, async/await uses the language&apos;s own <Code>try/catch</Code> — familiar from any synchronous code:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `try {\n  const order = await createOrder();\n} catch (error) {\n  console.log(error);\n}` }]} defaultLanguage="javascript" id="aa-trycatch" />
   <Callout tone="info">When <Code>await</Code> hits a rejected promise, it <Mark>throws</Mark> — so the rejection is caught by the surrounding <Code>catch</Code> block.</Callout>
  </section>

  {/* 9. Production Example */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Example</SectionHeading>
   <Para>How most modern Node.js services express a workflow:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `async function placeOrder() {\n  try {\n    const order = await createOrder();\n    const payment = await processPayment(order);\n    const driver = await assignDriver(payment);\n    await notifyUser(driver);\n  } catch (error) {\n    console.log("Order Failed");\n  }\n}` }]} defaultLanguage="javascript" id="aa-prod" />
  </section>

  {/* 10. Engineering Tradeoffs: accidental sequential */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs</SectionHeading>
   <Para>The biggest trap: <Code>await</Code> makes everything look sequential, so independent calls are often run one after another by accident.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = await getUser();    // 1s\nconst orders = await getOrders(); // 1s\nconst offers = await getOffers(); // 1s\n// total ≈ 3 seconds — but these don't depend on each other!` }]} defaultLanguage="javascript" id="aa-seq" />
   <Para>Migrating from promises to async/await frequently <Mark>loses concurrency</Mark> like this — one of the most common backend performance bugs.</Para>
   <Callout tone="warning" title="Rule of thumb">Use sequential <Code>await</Code> only when a step truly depends on the previous result. Otherwise run them together.</Callout>
  </section>

  {/* 11. Performance Considerations: Promise.all */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations</SectionHeading>
   <Para>For independent calls, combine <Code>await</Code> with <Code>Promise.all</Code> so they run concurrently:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const [user, orders, offers] = await Promise.all([\n  getUser(),\n  getOrders(),\n  getOffers(),\n]);\n// total ≈ 1 second instead of 3` }]} defaultLanguage="javascript" id="aa-all" />
   <Para>Async/await does not eliminate the promise combinators — reach for them when the shape of the work calls for it:</Para>
   <div className="flex flex-wrap gap-2">{['Promise.all → wait for all', 'Promise.race → first to settle', 'Promise.any → first to fulfill', 'Promise.allSettled → all outcomes'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Callout tone="warning" title="await inside loops">A <Code>for…of</Code> loop with <Code>await</Code> inside runs iterations one at a time. If they&apos;re independent, map to promises and <Code>Promise.all</Code> them instead.</Callout>
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="await outside async">Using <Code>await</Code> in a non-async function is a syntax error (outside top-level module scope).</Callout>
   <Callout tone="warning" title="Ignoring errors">Forgetting <Code>try/catch</Code> around awaited calls leaves rejections unhandled.</Callout>
   <Callout tone="warning" title="Accidental sequencing">Awaiting independent calls one by one instead of with <Code>Promise.all</Code>.</Callout>
   <Callout tone="warning" title="await in loops">Serializing independent iterations inside a <Code>for</Code> loop, making it needlessly slow.</Callout>
   <Callout tone="warning" title="Forgetting it returns a promise">An async function&apos;s caller still gets a promise — it must be awaited or chained.</Callout>
  </section>

  {/* 13. Debugging Stories */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <Callout tone="info" title="The endpoint that got 3x slower">
    After a &quot;cleanup&quot; refactor from <Code>Promise.all</Code> to a series of <Code>await</Code>s, an endpoint&apos;s latency tripled. The calls were independent; awaiting them in sequence serialized three 1-second requests into three seconds. Restoring <Code>Promise.all</Code> fixed it.
   </Callout>
   <Callout tone="info" title="The unhandled rejection">
    A function &quot;sometimes&quot; crashed the process. The cause was calling an async function without awaiting it or attaching <Code>.catch()</Code> — the rejected promise became an unhandled rejection. Awaiting it inside a <Code>try/catch</Code> surfaced and handled the error.
   </Callout>
  </section>

  {/* 14. TypeScript Example */}
  <section className="mb-12">
   <SectionHeading n={14}>TypeScript Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `interface User {\n  id: number;\n  name: string;\n}\n\nasync function getUser(): Promise<User> {\n  return { id: 1, name: "Sachin" };\n}\n\nconst user = await getUser();` }]} defaultLanguage="typescript" id="aa-ts" />
   <Para>After <Code>await</Code>, TypeScript automatically infers the type as <Code>User</Code> instead of <Code>Promise&lt;User&gt;</Code> — clean types with no extra annotations.</Para>
  </section>

  {/* 15. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={15}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What does async do?', 'What does await do?', 'Does async always return a promise?', 'Is await blocking?', 'Does async/await replace promises?', 'How does await work internally?', 'Why do we still need Promise.all?', 'Why can async/await reduce performance?', 'What if await hits a rejected promise?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="Does an async function always return a promise?">
    Yes — always. Returning a plain value wraps it in <Code>Promise.resolve(value)</Code>; throwing produces a rejected promise. The caller always receives a promise.
   </QA>
   <QA q="Is await blocking?">
    It blocks only the current async function — lines after it wait for the promise. It does not block the JavaScript runtime, event loop, or browser/Node, which keep handling other work.
   </QA>
   <QA q="Does async/await replace promises?">
    No. It&apos;s syntactic sugar over promises. Internally async/await → promises → event loop; everything is still promise-based, which is why <Code>Promise.all/race/any</Code> remain essential.
   </QA>
   <QA q="What happens when await encounters a rejected promise?">
    The <Code>await</Code> expression throws the rejection reason, so it propagates like a normal exception and can be caught with <Code>try/catch</Code>.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={16}>Mental Model</SectionHeading>
   <Para>With promises you call the restaurant and register a callback to be notified later. With async/await you place the order and simply &quot;wait for the food&quot; on that line, then continue — while everyone else in the building carries on. Same machinery, far easier for humans to read.</Para>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={17}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Async/await is syntactic sugar built on top of promises that lets asynchronous code be written in a synchronous-looking, more readable manner.
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={18}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Async/await didn&apos;t replace promises — it made promise-based code easier to read and maintain. Every async function returns a promise, and every await pauses only the current async function, not the entire runtime. Understanding promises first is essential, because async/await is built directly on top of them.
   </Callout>
  </section>
 </div>
);

export default JSAsyncAwait;
